import { CommonModule, Location } from '@angular/common';
import { ChangeDetectorRef, Component, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { userRoles } from '../../../environments/environment';
import { ButtonComponent } from '../../shared/components/custom-button/button.component';
import { TableComponent } from '../../shared/components/custom-table/table.component';
import { AlertDialogComponent, AlertDialogData } from '../../shared/components/dialog/alert-dialog/alert-dialog.component';
import { CommonDialogComponent } from '../../shared/components/dialog/commom/commom-dialog.component';
import { CSQViewDialogComponent } from '../../shared/components/dialog/view-csq-details/view-dialog.component';
import { InputComponent } from '../../shared/components/from-input/input.component';
import { CustomToastComponent } from '../../shared/components/toast/custom-toast.component';
import { HttpService } from '../../shared/services/http.service';

interface CSQTeamMember {
    totalRecords: any;
    id: any;
    items: boolean;
    memberId: string;
    firstName: string;
    lastName: string;
    email: string;
    mobileNumber: string;
    status: string;
    message: string;
    userId: string;
    totalRecord: any;
    isCSQAdmin?: boolean;
}

interface UsersPagedResponse {
    items: CSQTeamMember[];
    totalRecords?: number;
    totalRecord?: number;
}

interface APIResponse {
    status: boolean;
    message: string;
    data: CSQTeamMember[];
}

function isPagedResponse(
    data: UsersPagedResponse | CSQTeamMember[]
): data is UsersPagedResponse {
    return !!(data as UsersPagedResponse).items;
}

@Component({
    selector: 'app-csq-team',
    standalone: true,
    imports: [
        CommonModule,
        TableComponent,
        ButtonComponent,
        MatDialogModule,
        ReactiveFormsModule,
        InputComponent,
        FormsModule
    ],
    templateUrl: './csq-team.component.html',
    styleUrls: ['./csq-team.component.scss'],
})
export class CSQComponent {
    @ViewChild('actionButtons') actionButtonsTemplate!: TemplateRef<any>;
    @ViewChild('addCSQContent') addCSQContent!: TemplateRef<any>;
    @ViewChild(TableComponent) tableComponent!: TableComponent;

    constructor(
        private dialog: MatDialog,
        private fb: FormBuilder,
        private router: Router,
        private cdr: ChangeDetectorRef,
        private httpService: HttpService,
        private snackBar: MatSnackBar,
        private location: Location,
        private route: ActivatedRoute,

    ) {
        this.initForms();
    }

    private previousPage: number = 1;
    form!: FormGroup;
    userForm!: FormGroup;
    loading = false;
    submissionLoading = false;
    isSubmitting = new BehaviorSubject<boolean>(false);
    data: any[] = [];
    editingMember: CSQTeamMember | null = null;
    fullData: any[] = [];
    search: string = '';
    searchId: string = '';
    columns: string[] = ['first name', 'last name', 'email', 'phone number', 'status'];
    customErrorMessages = {
        firstName: {
            required: 'Please enter a first name',
            whitespace: 'Please enter a first name'
        },
        lastName: {
            required: 'Please enter a last name',
            whitespace: 'Please enter a last name'
        },
        email: {
            required: 'Please enter a valid email address.',
            email: 'Please enter a valid email address.',
            pattern: 'Please enter a valid email address.',
            whitespace: 'Please enter a valid email address.'
        },
        mobileNumber: {
            required: 'Please enter a phone number.',
            pattern: 'Please enter a valid phone number.',
            whitespace: 'Please enter a phone number.'
        },
    };
    userRoles = {
        addNew: false,
        listItem: {
            edit: false,
            view: false,
            delete: false,
            statusChange: false
        }
    }


    ngOnInit(): void {
        this.route.queryParams.subscribe(params => {
            this.searchId = params['search'] ?? null;
            this.tryOpenUserModal();
        });
        this.fetchCSQTeam();
        const user = localStorage.getItem('userRole');
        if (user && user in userRoles) {
            this.userRoles = userRoles[user as keyof typeof userRoles]?.csqTeam
        }

    }

    private initForms(): void {
        this.form = this.fb.group({
            firstName: ['', [
                Validators.required,
                this.noEmptyOrWhitespaceValidator()
            ]],
            lastName: ['', [
                Validators.required,
                this.noEmptyOrWhitespaceValidator()
            ]],
            email: ['', [
                Validators.required,
                Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
                this.noEmptyOrWhitespaceValidator()
            ]],
            mobileNumber: [
                '',
                [
                    Validators.required,
                    Validators.pattern(/^(04\d{8}|\+614\d{8})$/),
                    this.noEmptyOrWhitespaceValidator()
                ]
            ],
            isCSQAdmin: [false]
        });

        this.userForm = this.fb.group({
            firstName: [''],
            lastName: [''],
            email: [''],
            status: [''],
        });
    }

    private noEmptyOrWhitespaceValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (control.value === null || control.value === undefined || control.value === '') {
                return { required: true };
            }
            if (typeof control.value === 'string' && control.value.trim().length === 0) {
                return { whitespace: true };
            }

            return null;
        };
    }

    updateSearch(searchValue: string): void {
        this.search = searchValue;
        this.applyFilter();
    }


    fetchCSQTeam(page: number = 1): void {
        this.loading = true;

        if (this.tableComponent) {
            this.previousPage = this.tableComponent.currentPage;
        }

        this.httpService.get<APIResponse>('csqusers').subscribe({
            next: (response) => {

                this.fullData = response?.data?.map(member => ({
                    id: member.memberId,
                    userId: member.userId,
                    'first name': member.firstName || '-',
                    'last name': member.lastName || '-',
                    email: member.email || '-',
                    'phone number': member.mobileNumber || '-',
                    status: member.status ?? 'Active',
                    firstName: member.firstName || '-',
                    lastName: member.lastName || '-',
                    mobileNumber: member.mobileNumber || '-',
                    isCSQAdmin: member.isCSQAdmin || false,
                    isStatusClickable: true,
                }));

                this.applyFilter();
                this.tryOpenUserModal()
            },
            error: (err) => {
                console.error('Failed to load CSQ team:', err);
                this.fullData = [];
                this.data = [];
                this.loading = false;
                this.showErrorToast(err?.message);

                if (this.tableComponent) {
                    this.tableComponent.currentPage = 1;
                }
            },
            complete: () => {
                this.loading = false;
            }
        });
    }

    applyFilter(): void {
        if (!this.search || this.search.trim() === '') {
            this.data = [...this.fullData];
            return;
        }
        // Match only the columns on screen: the rows also carry ids, GUIDs and internal flags,
        // and searching those makes rows with no visible match appear. Collapse whitespace on
        // both sides so text copied off the screen still matches what is stored.
        const normalize = (value: unknown): string =>
            (value ?? '')
                .toString()
                .toLowerCase()
                .replace(/\s+/g, ' ')
                .trim();

        const searchTerm = normalize(this.search);
        this.data = this.fullData.filter(item =>
            this.columns.some(column => normalize(item[column]).includes(searchTerm))
        );
        this.resetPagination();
        this.cdr.detectChanges();
    }

    private resetPagination(): void {
        this.data = [...this.data];
        if (this.tableComponent) {
            this.tableComponent.currentPage = 1;
            this.tableComponent.cdr?.detectChanges();
        }
    }


    tryOpenUserModal(): void {
        if (!this.searchId || !this.fullData?.length) return;

        // Match by memberId (id) or userId — global search may return either
        const searchedItem = this.fullData.find(
            item => item.id == this.searchId || item.userId == this.searchId
        );

        if (searchedItem) {
            this.dialog.open(CSQViewDialogComponent, {
                minWidth: '50rem',
                minHeight: '30rem',
                data: searchedItem,
            });
        }
    }


    saveMember(dialogRef: any): void {
        if (this.submissionLoading) return
        this.submissionLoading = true
        this.isSubmitting.next(true);

        const payload = {
            firstName: this.form.value.firstName,
            lastName: this.form.value.lastName,
            email: this.form.value.email,
            mobileNumber: this.form.value.mobileNumber,
            isCSQAdmin: this.form.value.isCSQAdmin ?? false
        };

        const apiCall = this.editingMember ?
            this.httpService.put(`csqusers/${this.editingMember?.id}`, payload) :
            this.httpService.post('csqusers', payload);

        apiCall.subscribe({
            next: (response) => {
                const res = response as any;
                if (res.status) {
                    this.showSuccessToast(res.message);
                    this.fetchCSQTeam();
                    this.isSubmitting.next(false);
                    dialogRef.close();
                } else {
                    const fieldErrors = res.errors ? Object.values(res.errors).flat().join(' ') : null;
                    this.showErrorToast(fieldErrors || res.message);
                    this.isSubmitting.next(false);
                }
                this.submissionLoading = false
            },
            error: (err) => {
                this.isSubmitting.next(false);
                console.error('Failed to save member:', err);
                this.showErrorToast(err?.message);
                this.submissionLoading = false
            }
        });
    }

    onStatusUpdate(event: { row: any; id: any }): void {
        if (!this.userRoles.listItem.statusChange) return
        const { row } = event;
        const currentStatus = row.status;
        const newStatus = currentStatus === 'Active' ? false : true

        const dialogData: AlertDialogData = {
            title: 'Are you sure!',
            message: `Are you sure you want to ${currentStatus === 'Active' ? 'Deactivate' : 'Activate'} ?`,
            okText: 'Confirm',
            cancelText: 'Cancel',
            icon: 'warning',
            submessage: ""
        };

        this.dialog.open(AlertDialogComponent, { width: '25.438rem', data: dialogData })
            .afterClosed().subscribe(result => {
                if (result) {
                    this.httpService.put<CSQTeamMember>(`users/${row?.userId}/toggle-status`, { status: newStatus })
                        .subscribe({
                            next: (response) => {
                                if (response.status) {
                                    this.updateSchoolInArrays(row.id, { status: newStatus ? 'Active' : 'Inactive' });
                                    this.showSuccessToast(response.message);
                                } else {
                                    this.showErrorToast(response.message);
                                }
                            },
                            error: (err) => this.showErrorToast(err?.message)

                        });
                }
            });
    }

    private updateSchoolInArrays(schoolId: string, updates: any): void {
        const fullDataIndex = this.fullData.findIndex(school => school.id === schoolId);
        if (fullDataIndex !== -1) {
            this.fullData[fullDataIndex] = { ...this.fullData[fullDataIndex], ...updates };
        }
        const dataIndex = this.data.findIndex(school => school.id === schoolId);
        if (dataIndex !== -1) {
            this.data[dataIndex] = { ...this.data[dataIndex], ...updates };
            this.data = [...this.data];
        }
    }

    openAddDialog(memberToEdit: CSQTeamMember | null = null): void {
        this.editingMember = memberToEdit;
        if (memberToEdit) {
            this.form.patchValue({
                firstName: (memberToEdit as any)['first name'] ?? (memberToEdit as any).firstName ?? '',
                lastName: (memberToEdit as any)['last name'] ?? (memberToEdit as any).lastName ?? '',
                email: (memberToEdit as any).email ?? '',
                mobileNumber: (memberToEdit as any)['phone number'] ?? (memberToEdit as any).phoneNumber ?? '',
                isCSQAdmin: memberToEdit.isCSQAdmin ?? false
            });
        } else {
            this.form.reset();
        }

        const dialogRef = this.dialog.open(CommonDialogComponent, {
            data: {
                title: memberToEdit ? 'Edit CSQ team member ' : 'Add new CSQ team member',
                confirmText: memberToEdit ? 'Update' : 'Submit',
                cancelText: 'Cancel',
                template: this.addCSQContent,
                loading: false
            },
            width: '50.5rem',
            maxWidth: '100vw',
            disableClose: true,
            autoFocus: false
        });

        const subscription = this.isSubmitting.subscribe((isSubmitting: boolean | undefined) => {
            dialogRef.componentInstance.data.loading = isSubmitting;
            dialogRef.componentInstance.data.confirmText = memberToEdit ?
                (isSubmitting ? 'Updating...' : 'Update') :
                (isSubmitting ? 'Submitting...' : 'Submit');
        });

        dialogRef.afterClosed().subscribe(() => {
            subscription.unsubscribe();
        });

        dialogRef.componentInstance.confirmed.subscribe((confirmed: boolean) => {
            if (confirmed && this.form.valid) {
                this.saveMember(dialogRef);
            } else if (confirmed) {
                this.markFormGroupTouched(this.form);
                this.scrollToFirstInvalidControl();
            }
        });
    }

    onEdit(member: CSQTeamMember): void {
        this.openAddDialog(member);
    }

    onDelete(member: CSQTeamMember): void {
        const dialogRef = this.dialog.open(AlertDialogComponent, {
            width: '25.438rem',
            data: {
                title: 'Confirm delete',
                message: `Are you sure you want to delete ?`,
                okText: 'Delete',
                cancelText: 'Cancel',
                icon: 'warning',
                submessage: ""
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.httpService.delete<APIResponse>(`csqusers/${member.id}`).subscribe({
                    next: (response) => {
                        if (response.status) {
                            this.fetchCSQTeam();
                            this.showSuccessToast(response.message);
                        }
                        else {
                            this.showErrorToast(response.message);
                            return;
                        }
                    },
                    error: (err) => {
                        console.error('Failed to delete member:', err);
                        this.showErrorToast(`Failed to delete ${member.firstName} ${member.lastName}. Please try again.`);
                    },
                });
            }
        });
    }

    onView(user: any): void {
        this.dialog.open(CSQViewDialogComponent, {
            minWidth: '50rem',
            minHeight: '30rem',
            data: user,
        });
    }

    goBack() {
        this.location.back();
    }

    private markFormGroupTouched(formGroup: FormGroup) {
        Object.values(formGroup.controls).forEach(control => {
            control.markAsTouched();
            if (control instanceof FormGroup) {
                this.markFormGroupTouched(control);
            }
        });
    }

    private scrollToFirstInvalidControl() {
        const firstInvalidControl = document.querySelector('.ng-invalid');
        firstInvalidControl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    private adjustPaginationAfterDelete(): void {
        if (!this.tableComponent) return;

        const totalPages = Math.ceil(this.data.length / this.tableComponent.pageSize);
        if (this.tableComponent.currentPage > totalPages && totalPages > 0) {
            this.tableComponent.currentPage = totalPages;
        }
    }

    private showSuccessToast(message: string): void {
        this.snackBar.openFromComponent(CustomToastComponent, {
            data: { message },
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['no-padding-snackbar', 'success-toast']
        });
    }

    private showErrorToast(message: string | null | undefined): void {
        if (!message) return;
        this.snackBar.openFromComponent(CustomToastComponent, {
            data: { message },
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['no-padding-snackbar', 'error-toast']
        });
    }
}